import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { FaCheckCircle, FaSpinner, FaHome, FaWallet, FaCrown, FaCalendar, FaTag } from 'react-icons/fa';
import { message } from 'antd';
import axios from 'axios';
import {BASE_API} from "../../Api/Api"

const PaymentStatus = () => {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [status, setStatus] = useState('processing');
    const [paymentData, setPaymentData] = useState(null);
    const BASE_URL = BASE_API;

    useEffect(() => {
        verifyPayment();
    }, []);

    const verifyPayment = async () => {
        const razorpayOrderId = searchParams.get('order_id');
        const razorpayPaymentId = searchParams.get('razorpay_payment_id');
        const razorpaySignature = searchParams.get('razorpay_signature');
        const statusParam = searchParams.get('status');

        // ✅ URL se amount aur plan name lo
        const urlAmount = searchParams.get('amount');
        const urlPlanName = searchParams.get('plan_name');

        // ✅ Already verified - URL params se data lo
        if (statusParam === 'success' && razorpayPaymentId) {
            setStatus('success');
            setPaymentData({
                plan_name: urlPlanName
                    ? decodeURIComponent(urlPlanName)
                    : 'Premium Plan',
                amount: urlAmount ? Number(urlAmount) : 0,
                payment_id: razorpayPaymentId,
                order_id: razorpayOrderId,
            });
            setLoading(false);
            return;
        }

        if (!razorpayOrderId || !razorpayPaymentId) {
            setStatus('failed');
            setLoading(false);
            return;
        }

        try {
            const response = await axios.post(
                `${BASE_URL}/verify-payment/`,
                {
                    razorpay_order_id: razorpayOrderId,
                    razorpay_payment_id: razorpayPaymentId,
                    razorpay_signature: razorpaySignature || ''
                },
                { headers: { 'Content-Type': 'application/json' } }
            );

            if (response.data.success) {
                setStatus('success');
                setPaymentData({
                    plan_name: response.data.plan_name || urlPlanName || 'Premium Plan',
                    amount: response.data.amount || (urlAmount ? Number(urlAmount) : 0),
                    payment_id: razorpayPaymentId,
                    order_id: razorpayOrderId,
                    expiry_date: response.data.expiry_date || null,
                    plan_type: response.data.plan_type || null,
                });
                message.success("🎉 Payment Successful!");
            } else {
                setStatus('failed');
                message.error(response.data.error || "Verification failed");
            }
        } catch (error) {
            console.error("Verify error:", error);
            setStatus('failed');
        } finally {
            setLoading(false);
        }
    };

    // ✅ Amount format karo
    const formatAmount = () => {
        const amt = paymentData?.amount;
        if (amt && Number(amt) > 0) {
            return `₹${Number(amt).toLocaleString('en-IN')}`;
        }
        // URL se bhi try karo
        const urlAmt = searchParams.get('amount');
        if (urlAmt && Number(urlAmt) > 0) {
            return `₹${Number(urlAmt).toLocaleString('en-IN')}`;
        }
        return 'N/A';
    };

    const getPlanName = () => {
        if (paymentData?.plan_name && paymentData.plan_name !== 'Premium Plan') {
            return paymentData.plan_name;
        }
        const urlPlanName = searchParams.get('plan_name');
        if (urlPlanName) return decodeURIComponent(urlPlanName);
        return paymentData?.plan_name || 'Premium Plan';
    };

    // ============================================
    // LOADING
    // ============================================
    if (loading) {
        return (
            <div style={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                minHeight: '100vh',
                flexDirection: 'column',
                gap: '20px',
                background: 'linear-gradient(135deg, #fff0f3, #fff)'
            }}>
                <div style={{
                    width: 80, height: 80, borderRadius: '50%',
                    background: 'linear-gradient(135deg, #ff0055, #ff4d88)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    boxShadow: '0 10px 28px rgba(255,0,85,.25)'
                }}>
                    <FaSpinner size={36} style={{ color: '#fff', animation: 'spin 1s linear infinite' }} />
                </div>
                <div style={{ textAlign: 'center' }}>
                    <p style={{ color: '#1a0a10', fontSize: 18, fontWeight: 700, margin: 0 }}>
                        Verifying Payment...
                    </p>
                    <p style={{ color: '#888', fontSize: 14, margin: '6px 0 0' }}>
                        Please wait, do not close this page
                    </p>
                </div>
                <style>{`
                    @import url('https://fonts.googleapis.com/css2?family=Poppins:wght@400;600;700;800&display=swap');
                    * { font-family: 'Poppins', sans-serif !important; }
                    @keyframes spin { 0%{transform:rotate(0deg)} 100%{transform:rotate(360deg)} }
                `}</style>
            </div>
        );
    }

    // ============================================
    // SUCCESS
    // ============================================
    if (status === 'success') {
        return (
            <>
                <style>{`
                    @import url('https://fonts.googleapis.com/css2?family=Poppins:wght@400;600;700;800;900&display=swap');
                    * { font-family: 'Poppins', sans-serif !important; box-sizing: border-box; margin: 0; }
                    @keyframes pulse { 0%,100%{transform:scale(1)} 50%{transform:scale(1.05)} }
                    @keyframes fadeIn { from{opacity:0;transform:translateY(20px)} to{opacity:1;transform:translateY(0)} }
                    @keyframes spin { 0%{transform:rotate(0deg)} 100%{transform:rotate(360deg)} }
                    .ps-btn:hover { transform: translateY(-2px) !important; }
                `}</style>

                <div style={{
                    display: 'flex', justifyContent: 'center', alignItems: 'center',
                    minHeight: '100vh',
                    background: 'linear-gradient(135deg, #f0fdf4, #dcfce7, #fff)',
                    padding: '20px'
                }}>
                    <div style={{
                        background: '#fff',
                        borderRadius: '28px',
                        padding: '48px 40px',
                        maxWidth: '520px',
                        width: '100%',
                        textAlign: 'center',
                        boxShadow: '0 24px 64px rgba(76,175,80,.12)',
                        border: '2px solid #a5d6a7',
                        animation: 'fadeIn .5s ease'
                    }}>
                        {/* Success Icon */}
                        <div style={{
                            width: 110, height: 110, borderRadius: '50%',
                            background: 'linear-gradient(135deg, #4caf50, #66bb6a)',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            margin: '0 auto 24px',
                            boxShadow: '0 16px 40px rgba(76,175,80,.3)',
                            animation: 'pulse 1.5s ease-in-out infinite'
                        }}>
                            <FaCheckCircle size={54} style={{ color: '#fff' }} />
                        </div>

                        <h2 style={{
                            fontSize: 30, fontWeight: 900, color: '#1a0a10',
                            marginBottom: 8
                        }}>
                            Payment Successful! 🎉
                        </h2>
                        <p style={{ color: '#666', fontSize: 15, marginBottom: 28, lineHeight: 1.6 }}>
                            Your plan has been activated successfully.<br />
                            Enjoy your premium features!
                        </p>

                        {/* Payment Details Card */}
                        <div style={{
                            background: 'linear-gradient(135deg, #f0fdf4, #dcfce7)',
                            borderRadius: 18,
                            padding: '20px 24px',
                            marginBottom: 28,
                            textAlign: 'left',
                            border: '1.5px solid #a5d6a7'
                        }}>
                            {/* Plan */}
                            <div style={{
                                display: 'flex', justifyContent: 'space-between',
                                padding: '10px 0',
                                borderBottom: '1px solid #c8e6c9'
                            }}>
                                <span style={{ color: '#555', fontSize: 13, display: 'flex', alignItems: 'center', gap: 6 }}>
                                    <FaTag size={11} style={{ color: '#4caf50' }} /> Plan
                                </span>
                                <span style={{ fontWeight: 800, color: '#1a0a10', fontSize: 14 }}>
                                    {getPlanName()}
                                </span>
                            </div>

                            {/* Amount */}
                            <div style={{
                                display: 'flex', justifyContent: 'space-between',
                                padding: '10px 0',
                                borderBottom: '1px solid #c8e6c9'
                            }}>
                                <span style={{ color: '#555', fontSize: 13, display: 'flex', alignItems: 'center', gap: 6 }}>
                                    💰 Amount Paid
                                </span>
                                <span style={{
                                    fontWeight: 900, color: '#ff0055', fontSize: 18,
                                    background: 'linear-gradient(135deg, #ff0055, #ff4d88)',
                                    WebkitBackgroundClip: 'text',
                                    WebkitTextFillColor: 'transparent'
                                }}>
                                    {formatAmount()}
                                </span>
                            </div>

                            {/* Status */}
                            <div style={{
                                display: 'flex', justifyContent: 'space-between',
                                padding: '10px 0',
                                borderBottom: paymentData?.expiry_date ? '1px solid #c8e6c9' : 'none'
                            }}>
                                <span style={{ color: '#555', fontSize: 13 }}>Status</span>
                                <span style={{
                                    fontWeight: 700, color: '#2e7d32', fontSize: 13,
                                    display: 'flex', alignItems: 'center', gap: 5,
                                    padding: '3px 12px',
                                    background: '#e8f5e9',
                                    borderRadius: 999,
                                    border: '1px solid #a5d6a7'
                                }}>
                                    <FaCheckCircle size={11} /> Active
                                </span>
                            </div>

                            {/* Expiry Date */}
                            {paymentData?.expiry_date && (
                                <div style={{
                                    display: 'flex', justifyContent: 'space-between',
                                    padding: '10px 0',
                                    borderBottom: '1px solid #c8e6c9'
                                }}>
                                    <span style={{ color: '#555', fontSize: 13, display: 'flex', alignItems: 'center', gap: 6 }}>
                                        <FaCalendar size={11} style={{ color: '#4caf50' }} /> Valid Until
                                    </span>
                                    <span style={{ fontWeight: 700, color: '#1a0a10', fontSize: 13 }}>
                                        {new Date(paymentData.expiry_date).toLocaleDateString('en-IN', {
                                            day: '2-digit', month: 'short', year: 'numeric'
                                        })}
                                    </span>
                                </div>
                            )}

                            {/* Payment ID */}
                            {paymentData?.payment_id && (
                                <div style={{
                                    display: 'flex', justifyContent: 'space-between',
                                    padding: '10px 0'
                                }}>
                                    <span style={{ color: '#555', fontSize: 13 }}>Payment ID</span>
                                    <span style={{
                                        fontWeight: 600, fontSize: 11, color: '#777',
                                        fontFamily: 'monospace'
                                    }}>
                                        {paymentData.payment_id.substring(0, 18)}...
                                    </span>
                                </div>
                            )}
                        </div>

                        {/* Buttons */}
                        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                            <button
                                className="ps-btn"
                                onClick={() => navigate('/dashboard')}
                                style={{
                                    flex: 1, padding: '14px 16px',
                                    border: 'none', borderRadius: 14,
                                    background: 'linear-gradient(135deg, #ff0055, #ff4d88)',
                                    color: '#fff', fontWeight: 700, fontSize: 14,
                                    cursor: 'pointer',
                                    display: 'flex', alignItems: 'center',
                                    justifyContent: 'center', gap: 8,
                                    boxShadow: '0 8px 24px rgba(255,0,85,.25)',
                                    transition: 'all .3s ease'
                                }}
                            >
                                <FaHome size={15} /> Go to Dashboard
                            </button>
                            <button
                                className="ps-btn"
                                onClick={() => navigate('/recharge')}
                                style={{
                                    flex: 1, padding: '14px 16px',
                                    border: '2px solid #a5d6a7',
                                    borderRadius: 14,
                                    background: '#fff', color: '#2e7d32',
                                    fontWeight: 700, fontSize: 14,
                                    cursor: 'pointer',
                                    display: 'flex', alignItems: 'center',
                                    justifyContent: 'center', gap: 8,
                                    transition: 'all .3s ease'
                                }}
                            >
                                <FaCrown size={15} /> View Plans
                            </button>
                        </div>

                        {/* Thank you note */}
                        <p style={{
                            marginTop: 20, color: '#999', fontSize: 12,
                            lineHeight: 1.6
                        }}>
                            Thank you for your purchase! 💖<br />
                            A confirmation has been sent to your account.
                        </p>
                    </div>
                </div>
            </>
        );
    }

    // ============================================
    // FAILED
    // ============================================
    return (
        <>
            <style>{`
                @import url('https://fonts.googleapis.com/css2?family=Poppins:wght@400;600;700;800;900&display=swap');
                * { font-family: 'Poppins', sans-serif !important; box-sizing: border-box; margin: 0; }
                @keyframes fadeIn { from{opacity:0;transform:translateY(20px)} to{opacity:1;transform:translateY(0)} }
                .ps-btn:hover { transform: translateY(-2px) !important; }
            `}</style>

            <div style={{
                display: 'flex', justifyContent: 'center', alignItems: 'center',
                minHeight: '100vh',
                background: 'linear-gradient(135deg, #fff5f5, #fff)',
                padding: '20px'
            }}>
                <div style={{
                    background: '#fff',
                    borderRadius: '28px',
                    padding: '48px 40px',
                    maxWidth: '500px',
                    width: '100%',
                    textAlign: 'center',
                    boxShadow: '0 24px 64px rgba(244,67,54,.08)',
                    border: '2px solid #ffcdd2',
                    animation: 'fadeIn .5s ease'
                }}>
                    {/* Failed Icon */}
                    <div style={{
                        width: 110, height: 110, borderRadius: '50%',
                        background: 'linear-gradient(135deg, #f44336, #ef5350)',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        margin: '0 auto 24px',
                        boxShadow: '0 16px 40px rgba(244,67,54,.25)',
                        fontSize: 54
                    }}>
                        ❌
                    </div>

                    <h2 style={{
                        fontSize: 28, fontWeight: 900, color: '#1a0a10', marginBottom: 8
                    }}>
                        Payment Failed
                    </h2>
                    <p style={{ color: '#666', fontSize: 15, marginBottom: 28, lineHeight: 1.6 }}>
                        Something went wrong with your payment.<br />
                        Please try again or contact support.
                    </p>

                    {/* Info Box */}
                    <div style={{
                        background: '#fff5f5',
                        border: '1.5px solid #ffcdd2',
                        borderRadius: 14,
                        padding: '14px 18px',
                        marginBottom: 24,
                        fontSize: 13, color: '#c62828',
                        display: 'flex', alignItems: 'center', gap: 10
                    }}>
                        <span style={{ fontSize: 20 }}>ℹ️</span>
                        <span style={{ textAlign: 'left', fontWeight: 600 }}>
                            Your money was not deducted. If it was, it will be refunded within 5-7 business days.
                        </span>
                    </div>

                    <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                        <button
                            className="ps-btn"
                            onClick={() => navigate('/recharge')}
                            style={{
                                flex: 1, padding: '14px 16px',
                                border: 'none', borderRadius: 14,
                                background: 'linear-gradient(135deg, #ff0055, #ff4d88)',
                                color: '#fff', fontWeight: 700, fontSize: 14,
                                cursor: 'pointer',
                                display: 'flex', alignItems: 'center',
                                justifyContent: 'center', gap: 8,
                                boxShadow: '0 8px 24px rgba(255,0,85,.25)',
                                transition: 'all .3s ease'
                            }}
                        >
                            <FaWallet size={15} /> Try Again
                        </button>
                        <button
                            className="ps-btn"
                            onClick={() => navigate('/dashboard')}
                            style={{
                                flex: 1, padding: '14px 16px',
                                border: '2px solid #ffe4ec',
                                borderRadius: 14,
                                background: '#fff', color: '#555',
                                fontWeight: 700, fontSize: 14,
                                cursor: 'pointer',
                                display: 'flex', alignItems: 'center',
                                justifyContent: 'center', gap: 8,
                                transition: 'all .3s ease'
                            }}
                        >
                            <FaHome size={15} /> Dashboard
                        </button>
                    </div>
                </div>
            </div>
        </>
    );
};

export default PaymentStatus;